import { createFonts } from "../fonts/fonts";

import type { TextStyle } from "react-native";
import type { ThemeFontStyle } from "../fonts/types";
import type {
  TextVariationBodySize,
  TextVariationCapsSize,
  TextVariationCaptionSize,
  TextVariationTitleSize,
  ThemeText,
  createTextAliasesFn,
  createTextFn,
  createTextGlobalsFn,
} from "./types";

const addWeights = <
  TSizeType extends string,
  TSize extends Record<TSizeType, TextStyle>,
>(
  sizes: TSize,
  weight: ThemeFontStyle,
) => {
  return (Object.keys(sizes) as TSizeType[]).reduce(
    (acc, size) => ({
      ...acc,
      [size]: {
        ...sizes[size],
        ...weight,
      },
    }),
    {} as Record<TSizeType, TextStyle>,
  );
};

export const createTextGlobals: createTextGlobalsFn = (os) => {
  const fonts = createFonts(os);

  const titleSizes: Record<TextVariationTitleSize, TextStyle> = {
    one: {
      fontSize: 32,
      lineHeight: 36,
    },
    two: {
      fontSize: 24,
      lineHeight: 32,
    },
    three: {
      fontSize: 20,
      lineHeight: 28,
    },
    four: {
      fontSize: 18,
      lineHeight: 24,
    },
    five: {
      fontSize: 16,
      lineHeight: 20,
    },
    six: {
      fontSize: 14,
      lineHeight: 20,
    },
  };

  const bodySizes: Record<TextVariationBodySize, TextStyle> = {
    small: {
      fontSize: 12,
      lineHeight: 20,
    },
    medium: {
      fontSize: 16,
      lineHeight: 24,
    },
    large: {
      fontSize: 20,
      lineHeight: 28,
    },
  };

  const captionSizes: Record<TextVariationCaptionSize, TextStyle> = {
    small: {
      fontSize: 10,
      lineHeight: 16,
    },
    medium: {
      fontSize: 12,
      lineHeight: 20,
    },
    large: {
      fontSize: 14,
      lineHeight: 20,
    },
  };

  const capsSizes: Record<TextVariationCapsSize, TextStyle> = {
    small: {
      fontSize: 10,
      lineHeight: 16,
    },
    medium: {
      fontSize: 12,
      lineHeight: 16,
    },
    large: {
      fontSize: 14,
      lineHeight: 20,
    },
  };

  return {
    regular: {
      body: addWeights(bodySizes, fonts.regular),
      caption: addWeights(captionSizes, fonts.regular),
      caps: addWeights(capsSizes, fonts.regular),
    },
    semibold: {
      title: addWeights(titleSizes, fonts.semibold),
      body: addWeights(bodySizes, fonts.semibold),
      caption: addWeights(captionSizes, fonts.semibold),
      caps: addWeights(capsSizes, fonts.semibold),
    },
    bold: {
      title: addWeights(titleSizes, fonts.bold),
    },
  };
};

export const createTextAliases: createTextAliasesFn = (os, globals) => {
  const button: TextStyle = globals.semibold.body.medium;

  return {
    button: {
      caps: {
        ...button,
        textTransform: "uppercase",
      },
      sentence: {
        ...button,
      },
    },
  };
};

export const createText: createTextFn = (os) => {
  const globals = createTextGlobals(os);
  const aliases = createTextAliases(os, globals);

  return {
    ...globals,
    ...aliases,
  };
};

export function convertToDotNotation(
  obj: Record<string, unknown>,
  parentKey: string = "",
  result: string[] = [],
): string[] {
  for (const key in obj) {
    if (typeof obj[key] === "object") {
      const currentKey = parentKey ? `${parentKey}.${key}` : key;
      convertToDotNotation((obj as any)[key], currentKey, result);
    } else {
      const currentKey = parentKey ? `${parentKey}` : key; // Change this line
      result.push(currentKey);
    }
  }
  return [...new Set(result)];
}

export function dotNotationToStyles(
  variation: string,
  text: ThemeText,
): TextStyle {
  const [weightOrAlias, variant, size] = variation.split(".");
  let style: TextStyle = {};
  if (["regular", "semibold", "bold"].includes(weightOrAlias)) {
    // e.g. regular, semibold, bold

    style = (text as any)[weightOrAlias][variant][size] as TextStyle;
  } else {
    // alias
    style = (text as any)[weightOrAlias][variant] as TextStyle;
  }

  return style;
}
